import {
    useAPIQuery,
    useGlobalAPIQueryContext,
} from "@dev2_techwalnut/tw-api-query-hook";
import { Fragment, useEffect } from "react";
import { useForm } from "react-hook-form";
import secureLocalStorage from "react-secure-storage";

const QCAcceptModal = ({ qcReportData, qcBatchId }) => {
    const userID = secureLocalStorage.getItem("userID");
    const { invalidateApi } = useGlobalAPIQueryContext();

    const { useUpdate: updateQCBatch } = useAPIQuery({
        key: "qc-batch-dropdown",
        endpoint: "/api/check-batch/",
    });

    const { mutate: updateQcBatchData, isPending: updatingQCBatch } =
        updateQCBatch({
            successMessageFromServer: true,
            errorMessageFromServer: true,
            onSuccess: () => {
                invalidateApi({ key: "qc-report" });
                invalidateApi({ key: "api/check-batch" });
            },
            onError: () => {
                invalidateApi({ key: "qc-report" });
                invalidateApi({ key: "api/check-batch" });
            },
        });

    const {
        handleSubmit,
        register,
        watch,
        reset,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm({ mode: "onBlur" });

    const accepted = watch("accepted_count");
    const onHold = watch("on_hold_count");
    const rejected = watch("rejection_count");

    const onSubmit = (data) => {
        const total =
            Number(data.accepted_count || 0) +
            Number(data.on_hold_count || 0) +
            Number(data.rejection_count || 0);

        if (qcReportData && total > qcReportData.received_quantity) {
            setError("combined_total", {
                type: "manual",
                message: `The combined total (${total}) cannot exceed the received quantity (${qcReportData.received_quantity}).`,
            });
            return;
        }

        clearErrors("combined_total");
        console.log("✅ Submitted:", data);

        updateQcBatchData({
            id: qcBatchId,
            data: {
                accepted_count: Number(data.accepted_count || 0),
                on_hold_count: Number(data.on_hold_count || 0),
                rejection_count: Number(data.rejection_count || 0),
                qc_result: 1,
                updated_by: userID,
            },
        });
    };

    // Watch input values and validate live
    useEffect(() => {
        if (!qcReportData) return;

        const total =
            Number(accepted || 0) + Number(onHold || 0) + Number(rejected || 0);

        if (total > qcReportData.received_quantity) {
            setError("combined_total", {
                type: "manual",
                message: `The combined total (${total}) cannot exceed the received quantity (${qcReportData.received_quantity}).`,
            });
        } else if (total < qcReportData.received_quantity) {
            setError("combined_total", {
                type: "manual",
                message: `The combined total (${total}) cannot be less than the received quantity (${qcReportData.received_quantity}).`,
            });
        } else {
            clearErrors("combined_total");
        }
    }, [accepted, onHold, rejected, qcReportData, setError, clearErrors]);

    // Reset form with defaults
    useEffect(() => {
        if (qcReportData) {
            reset({
                accepted_count: qcReportData?.received_quantity,
                on_hold_count: 0,
                rejection_count: 0,
            });
        }
    }, [qcReportData, reset]);

    // Clean modal backdrops on unmount
    useEffect(() => {
        return () => {
            const backdrop = document.querySelector(".modal-backdrop");
            if (backdrop) {
                backdrop.remove();
                document.body.classList.remove("modal-open");
                document.body.style = "";
            }
        };
    }, []);

    return (
        <Fragment>
            <div
                className="modal fade m-0 p-0"
                tabIndex={-1}
                id="accept-qc-modal"
                aria-modal="true"
            >
                <div className="modal-dialog modal-lg" role="document">
                    <div className="modal-content">
                        <button
                            className="close"
                            data-bs-dismiss="modal"
                            id="po_modal_close_btn"
                        >
                            <em
                                className="icon ni ni-cross"
                                aria-hidden="true"
                            ></em>
                        </button>
                        <div className="modal-header">
                            <h5 className="modal-title">Accept QC</h5>
                        </div>

                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            autoComplete="off"
                        >
                            <div className="modal-body">
                                {errors.combined_total && (
                                    <div className="alert alert-danger py-2">
                                        {errors.combined_total.message}
                                    </div>
                                )}

                                <div className="row">
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <label className="form-label">
                                            Accepted
                                            <span className="ms-1 fs-8 text-danger">
                                                *
                                            </span>
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder="Enter accepted count"
                                            step={"any"}
                                            {...register("accepted_count", {
                                                required:
                                                    "Accepted count is required",
                                                validate: {
                                                    positive: (v) =>
                                                        v >= 0 ||
                                                        "Only positive numbers are allowed",
                                                },
                                                max: {
                                                    value:
                                                        qcReportData?.received_quantity ||
                                                        0,
                                                    message: `Accepted count cannot be more than received quantity (${qcReportData?.received_quantity})`,
                                                },
                                            })}
                                            onWheel={(e) => e.target.blur()}
                                        />
                                        {errors.accepted_count && (
                                            <span className="fs-8 text-danger">
                                                {errors.accepted_count.message}
                                            </span>
                                        )}
                                    </div>

                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <label className="form-label">
                                            On Hold Count
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder="Enter on hold count"
                                            step="any"
                                            {...register("on_hold_count", {
                                                validate: {
                                                    positive: (v) =>
                                                        !v ||
                                                        v >= 0 ||
                                                        "Only positive numbers are allowed",
                                                },
                                                max: {
                                                    value:
                                                        qcReportData?.received_quantity ||
                                                        0,
                                                    message: `On hold count cannot be more than received quantity (${qcReportData?.received_quantity})`,
                                                },
                                            })}
                                            onWheel={(e) => e.target.blur()}
                                        />
                                        {errors.on_hold_count && (
                                            <span className="fs-8 text-danger">
                                                {errors.on_hold_count.message}
                                            </span>
                                        )}
                                    </div>

                                    <div className="col-lg-6 col-md-6 col-sm-12 mt-3">
                                        <label className="form-label">
                                            Rejection Count
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder="Enter rejection count"
                                            step="any"
                                            {...register("rejection_count", {
                                                validate: {
                                                    positive: (v) =>
                                                        !v ||
                                                        v >= 0 ||
                                                        "Only positive numbers are allowed",
                                                },
                                                max: {
                                                    value:
                                                        qcReportData?.received_quantity ||
                                                        0,
                                                    message: `Rejected count cannot be more than received quantity (${qcReportData?.received_quantity})`,
                                                },
                                            })}
                                            onWheel={(e) => e.target.blur()}
                                        />
                                        {errors.rejection_count && (
                                            <span className="fs-8 text-danger">
                                                {errors.rejection_count.message}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={
                                        !!errors.combined_total ||
                                        updatingQCBatch
                                    }
                                >
                                    {updatingQCBatch
                                        ? "Submitting..."
                                        : "Submit"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <button
                type="button"
                className="btn btn-primary mx-2"
                data-bs-toggle="modal"
                data-bs-target="#accept-qc-modal"
            >
                Accept
            </button>
        </Fragment>
    );
};

export default QCAcceptModal;
